import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Globe, Plus, Eye, Search, Filter, SlidersHorizontal, Download } from 'lucide-react';
import { RowMenu } from '../components/RowMenu';
import { RefreshButton } from '../_shared/AnalyticsKit';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, FilterBuilder, type ColumnDef, type FilterField, type FilterCondition } from '@vnetwork/ui/data';
import {
  Button, Skeleton, Badge,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter,
  Input,
  Popover, PopoverTrigger, PopoverContent,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuCheckboxItem,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useCDNs, useCDNDetail, useCreateCDN, useUpdateCDN, useDeleteCDN } from '@/api/media/shared-cdns';
import type { CDNConfig } from '@/api/media/configs';
import type { CDN } from '@/api/media/shared-cdns-types';
import { ActiveStatusPill } from './ActiveStatusPill';
import styles from './CdnEndpointsList.module.css';

// ─── Detail dialog ────────────────────────────────────────────────────────────

function CdnDetailDialog({ serviceId, cdnId, config, open, onOpenChange }: {
  serviceId: string; cdnId: number | null; config: CDNConfig;
  open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const { data, isLoading } = useCDNDetail(serviceId, config, cdnId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{config.labels.detailDialogTitle}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading && <div className={styles.loading}>Loading…</div>}
          {!isLoading && data && (
            <div className={styles.detailContent}>
              {([['Name', data.name], ['ID', `#${data.id}`], ['Host', data.host], ['Username', data.username], ['Entity Name', data.entity_name], ['Default IP Field', data.default_ip_field], ['Predefined', data.predefined ? 'Yes' : 'No']] as [string, string | undefined][]).map(([label, val]) => val ? (
                <div key={label}>
                  <div className={styles.fieldLabel}>{label}</div>
                  <div className={styles.fieldValue}>{val}</div>
                </div>
              ) : null)}
              <div>
                <div className={styles.fieldLabel}>Status</div>
                <ActiveStatusPill value={data.status} />
              </div>
              {data.updated ? (
                <div>
                  <div className={styles.fieldLabel}>Updated</div>
                  <div className={styles.fieldValueMuted}>{new Date(data.updated).toLocaleString()}</div>
                </div>
              ) : null}
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

// ─── Add / edit dialog ────────────────────────────────────────────────────────

const defaultForm = { name: '', host: '', username: '', password: '', entity_name: '', entity_type: 1, default_ip_field: '' };

function CdnAddEditDialog({ open, onOpenChange, serviceId, config, cdnId, onDone }: {
  open: boolean; onOpenChange: (v: boolean) => void;
  serviceId: string; config: CDNConfig;
  cdnId?: number; onDone: () => void;
}) {
  const { show } = useToast();
  const isEdit = !!cdnId;
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPw, setShowPw] = useState(false);

  const { data: detail, isLoading: detailLoading } = useCDNDetail(serviceId, config, cdnId ?? null);
  const create = useCreateCDN(serviceId, config);
  const update = useUpdateCDN(serviceId, config);
  const isPending = create.isPending || update.isPending;

  // Seed form when editing — password intentionally left blank (leave-blank-to-keep).
  useEffect(() => {
    if (detail) setForm({ name: detail.name || '', host: detail.host || '', username: detail.username || '', password: '', entity_name: detail.entity_name || '', entity_type: detail.entity_type || 1, default_ip_field: detail.default_ip_field || '' });
  }, [detail?.id]);

  const set = (k: string, v: string) => { setForm((p) => ({ ...p, [k]: v })); if (errors[k]) setErrors((p) => ({ ...p, [k]: '' })); };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'CDN name is required';
    if (!form.entity_name.trim()) e.entity_name = 'Entity name is required';
    if (!form.host.trim()) e.host = 'Host is required';
    if (!form.username.trim()) e.username = 'Username is required';
    // Leave-blank-to-keep: password only required on create or when explicitly changed.
    if (!form.password && !isEdit) e.password = 'Password is required';
    if (!form.default_ip_field.trim()) e.default_ip_field = 'Default IP field is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) { show({ tone: 'danger', title: 'Fill in all required fields.' }); return; }
    let ipField = form.default_ip_field.trim();
    if (ipField && !/^https?:\/\//i.test(ipField)) ipField = `http://${ipField}`;
    // Omit password from update payload when blank — API keeps the stored value.
    const { password, ...formWithoutPw } = form;
    const payload = isEdit && !password
      ? { ...formWithoutPw, default_ip_field: ipField }
      : { ...form, default_ip_field: ipField };
    try {
      if (isEdit) { await update.mutateAsync({ cdnId: cdnId!, payload }); show({ tone: 'success', title: 'CDN updated' }); }
      else { await create.mutateAsync(payload); show({ tone: 'success', title: 'CDN created' }); }
      onDone(); onOpenChange(false);
    } catch (e: unknown) { show({ tone: 'danger', title: (e as Error).message || 'Operation failed' }); }
  };

  const ftpPreview = `ftp://${form.username || 'username'}:${form.password ? '••••••' : isEdit ? '(unchanged)' : 'password'}@${form.host || 'ftp.example.com'}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? config.labels.updateDialogTitle : config.labels.addDialogTitle}</DialogTitle>
          <DialogDescription>{isEdit ? config.labels.updateDialogDescription(form.name) : config.labels.addDialogDescription}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {detailLoading ? <div className={styles.loading}>Loading…</div> : (
            <div className={styles.detailContent}>
              <Field label="CDN Name" required error={errors.name}>{(fp) => <Input {...fp} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Media Delivery CDN" />}</Field>
              <Field label="Entity Name" required error={errors.entity_name}>{(fp) => <Input {...fp} value={form.entity_name} onChange={(e) => set('entity_name', e.target.value)} placeholder="media-assets" />}</Field>
              <Field label="Entity Type">
                {(fp) => (
                  <Select value={String(form.entity_type)} disabled>
                    <SelectTrigger id={fp.id}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="1">Large File</SelectItem></SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Host" required error={errors.host}>{(fp) => <Input {...fp} value={form.host} onChange={(e) => set('host', e.target.value)} placeholder="ftp.example.com" />}</Field>
              <div className={styles.formGrid}>
                <Field label="Username" required error={errors.username}>{(fp) => <Input {...fp} value={form.username} onChange={(e) => set('username', e.target.value)} placeholder="media-admin" />}</Field>
                <Field label="Password" required={!isEdit} error={errors.password}>
                  {(fp) => (
                    <Input
                      {...fp}
                      type={showPw ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => set('password', e.target.value)}
                      placeholder={isEdit ? '••• (unchanged)' : 'Enter password'}
                      endSlot={
                        <button type="button" className={styles.revealBtn} aria-label={showPw ? 'Hide password' : 'Show password'} onClick={() => setShowPw(!showPw)}>
                          <Eye size={14} />
                        </button>
                      }
                    />
                  )}
                </Field>
              </div>
              <Field label="Default IP Field" required description="Protocol (http://) added automatically if omitted." error={errors.default_ip_field}>{(fp) => <Input {...fp} value={form.default_ip_field} onChange={(e) => set('default_ip_field', e.target.value)} placeholder="lfd.example.com" />}</Field>
              <div>
                <div className={styles.fieldLabel}>FTP Preview</div>
                <pre className={styles.ftpPreview}>{ftpPreview}</pre>
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isPending}>Cancel</Button>
          <Button onClick={() => void handleSubmit()} disabled={isPending || detailLoading}>{isPending ? 'Saving…' : isEdit ? 'Update CDN' : 'Create CDN'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── List ─────────────────────────────────────────────────────────────────────

// Columns the user can toggle via "Display options" (name + actions are fixed).
const TOGGLEABLE = [
  { id: 'id', label: 'ID' },
  { id: 'host', label: 'Host' },
  { id: 'entity_name', label: 'Entity Name' },
  { id: 'status', label: 'Status' },
  { id: 'predefined', label: 'Predefined' },
  { id: 'updated', label: 'Updated' },
];
const DEFAULT_VISIBLE: Record<string, boolean> = { id: true, host: true, entity_name: true, status: true, predefined: true, updated: true };

/** Download the given CDN endpoints as a CSV file. */
function exportCsv(rows: CDN[]) {
  const head = ['id', 'name', 'host', 'entity_name', 'status', 'predefined', 'updated'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows
    .map((r) =>
      [r.id, r.name, r.host, r.entity_name, r.status === 1 ? 'active' : 'inactive', r.predefined ? 'yes' : 'no', r.updated ?? '']
        .map(esc)
        .join(','),
    )
    .join('\n');
  const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `cdn-endpoints-${rows.length}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export interface CdnEndpointsListProps {
  serviceId: string;
  config: CDNConfig;
  /** Console-specific breadcrumb (root differs per console: Live Transcoding vs Cloud Encoding). */
  breadcrumbs?: ReactNode;
}

/** Config-driven CDN endpoint list for Cloud Encoding and Live Transcoding. */
export function CdnEndpointsList({ serviceId, config, breadcrumbs }: CdnEndpointsListProps) {
  const { show } = useToast();

  const { data: cdns = [], isLoading, refetch } = useCDNs(serviceId, config);
  const deleteMut = useDeleteCDN(serviceId, config);
  const loading = isLoading;

  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState<number | undefined>();
  const [detailId, setDetailId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CDN | null>(null);
  const [search, setSearch] = useState('');
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [visible, setVisible] = useState<Record<string, boolean>>(DEFAULT_VISIBLE);

  // Active scope derived from the FilterBuilder conditions (multiselect values
  // are comma-joined per row, so split + flatten across matching rows).
  const collect = (field: string) =>
    conditions
      .filter((c) => c.field === field)
      .flatMap((c) => (c.value || '').split(','))
      .map((s) => s.trim())
      .filter(Boolean);
  const statusFilters = useMemo(() => new Set(collect('status')), [conditions]);
  const predefinedFilters = useMemo(() => new Set(collect('predefined')), [conditions]);

  const filterFields: FilterField[] = [
    {
      value: 'status',
      label: 'Status',
      input: 'multiselect',
      operators: [{ value: 'is_any_of', label: 'is any of' }],
      options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }],
    },
    {
      value: 'predefined',
      label: 'Predefined',
      input: 'multiselect',
      operators: [{ value: 'is_any_of', label: 'is any of' }],
      options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }],
    },
  ];

  const rows = useMemo(() => {
    let r = cdns;
    if (statusFilters.size) r = r.filter((x) => statusFilters.has(x.status === 1 ? 'active' : 'inactive'));
    if (predefinedFilters.size) r = r.filter((x) => predefinedFilters.has(x.predefined ? 'yes' : 'no'));
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((x) => x.name.toLowerCase().includes(q) || (x.host ?? '').toLowerCase().includes(q));
    }
    return r;
  }, [cdns, statusFilters, predefinedFilters, search]);

  const columns = useMemo<ColumnDef<CDN, unknown>[]>(() => {
    const all: Record<string, ColumnDef<CDN, unknown>> = {
      id: { id: 'id', header: 'ID', cell: ({ row }) => <Button variant="ghost" size="sm" onClick={() => setDetailId(row.original.id)}>#{row.original.id}</Button> },
      host: { id: 'host', header: 'Host', cell: ({ row }) => <span className={styles.cellMono}>{row.original.host}</span> },
      entity_name: { id: 'entity_name', header: 'Entity Name', cell: ({ row }) => row.original.entity_name },
      status: { id: 'status', header: 'Status', cell: ({ row }) => <ActiveStatusPill value={row.original.status} /> },
      // Predefined is a category label, not a status — neutral/subtle Badge.
      predefined: { id: 'predefined', header: 'Predefined', cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.predefined ? 'Yes' : 'No'}</Badge> },
      updated: { id: 'updated', header: 'Updated', cell: ({ row }) => <span className={styles.cellMuted}>{row.original.updated ? new Date(row.original.updated).toLocaleString() : '—'}</span> },
    };
    const nameCol: ColumnDef<CDN, unknown> = { id: 'name', header: 'Name', cell: ({ row }) => <span className={styles.cellBold}>{row.original.name}</span> };
    const actionsCol: ColumnDef<CDN, unknown> = {
      id: 'actions', header: '',
      cell: ({ row }) => (
        <RowMenu
          items={[
            { label: 'Edit', onSelect: () => { setEditId(row.original.id); setAddOpen(true); } },
            { label: 'Delete', danger: true, onSelect: () => setPendingDelete(row.original) },
          ]}
        />
      ),
    };
    return [nameCol, ...TOGGLEABLE.filter((c) => visible[c.id]).map((c) => all[c.id]), actionsCol];
  }, [visible]);

  return (
    <>
      <Container size="xl">
        <PageHeader
          breadcrumbs={breadcrumbs}
          title={`${config.labels.pageTitle}${cdns.length ? ` (${cdns.length})` : ''}`}
          description={config.labels.pageDescription}
          actions={<Button startIcon={<Plus size={15} />} onClick={() => { setEditId(undefined); setAddOpen(true); }}>{config.labels.createButtonText}</Button>}
        />

        <Toolbar
          start={
            <Input
              placeholder="Search by name or host…"
              startSlot={<Search size={15} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.searchInput}
            />
          }
          end={
            <>
              <RefreshButton onRefresh={() => void refetch()} />
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="secondary" startIcon={<Filter size={15} />}>
                    Filters{conditions.length ? ` (${conditions.length})` : ''}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className={styles.filterPopoverContent}>
                  <FilterBuilder
                    fields={filterFields}
                    defaultValue={conditions.length ? conditions : undefined}
                    onApply={(c) => {
                      setConditions(c.filter((x) => x.value.trim()));
                      setFilterOpen(false);
                    }}
                    onClose={() => setFilterOpen(false)}
                  />
                </PopoverContent>
              </Popover>
              {conditions.length > 0 && (
                <Button variant="ghost" onClick={() => setConditions([])}>Clear</Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>Display options</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                  {TOGGLEABLE.map((c) => (
                    <DropdownMenuCheckboxItem
                      key={c.id}
                      checked={visible[c.id]}
                      onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {c.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="secondary"
                startIcon={<Download size={15} />}
                disabled={rows.length === 0}
                onClick={() => exportCsv(rows)}
              >
                Export
              </Button>
            </>
          }
        />

        {loading ? (
          <div className={styles.skeletonGrid}>
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => String(r.id)}
            onRowClick={(row) => setDetailId(row.id)}
            emptyState={
              // First-use shows a creation hint; once endpoints exist the empty
              // state means the search/filters excluded everything.
              cdns.length === 0 ? (
                <EmptyState
                  icon={<Globe size={20} />}
                  title="No CDN endpoints"
                  description="Add a CDN endpoint to start distributing content."
                  actions={
                    <Button startIcon={<Plus size={14} />} onClick={() => { setEditId(undefined); setAddOpen(true); }}>
                      {config.labels.createButtonText}
                    </Button>
                  }
                />
              ) : (
                'No CDN endpoints match your search or filters.'
              )
            }
            footer={cdns.length ? `Showing ${rows.length} of ${cdns.length}` : undefined}
          />
        )}
      </Container>

      <CdnAddEditDialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) setEditId(undefined); }} serviceId={serviceId} config={config} cdnId={editId} onDone={() => setAddOpen(false)} />
      <CdnDetailDialog serviceId={serviceId} cdnId={detailId} config={config} open={!!detailId} onOpenChange={(v) => { if (!v) setDetailId(null); }} />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(v) => { if (!v) setPendingDelete(null); }}
        title={config.labels.deleteDialogTitle}
        description={config.labels.deleteDialogDescription(pendingDelete?.name ?? '')}
        confirmLabel="Delete"
        destructive
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteMut.mutate({ cdnId: pendingDelete.id }, {
            onSuccess: () => { show({ tone: 'success', title: 'CDN deleted' }); setPendingDelete(null); },
            onError: (e) => show({ tone: 'danger', title: e.message || 'Failed to delete CDN' }),
          });
        }}
      />
    </>
  );
}
